// src/features/marketplace/pages/MarketHome.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { db } from '../../../firebase';
import { collection, query, orderBy, limit, onSnapshot, where } from 'firebase/firestore';
import { Search, MapPin, TrendingUp, Car, Pin } from 'lucide-react';
import { ListingCard } from '../components/ListingCard';

export const MarketHome = () => {
  const navigate = useNavigate();
  const [recentListings, setRecentListings] = useState([]);
  const [searchBrand, setSearchBrand] = useState("");
  const [searchLocation, setSearchLocation] = useState("");
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    // Get user location
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => {}
    );

    // Load recent listings
    const q = query(
      collection(db, "marketplace_listings"), 
      where("status", "==", "active"),
      orderBy("createdAt", "desc"), 
      limit(20)
    );
    return onSnapshot(q, (snap) => {
      const listings = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Sortuj: PINNED first, potem najnowsze
      const sorted = listings.sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });
      
      setRecentListings(sorted.slice(0, 6));
    });
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchBrand) params.set('brand', searchBrand);
    if (searchLocation) params.set('location', searchLocation);
    navigate(`/market/search?${params.toString()}`);
  };

  const pinnedCount = recentListings.filter(l => l.isPinned).length;
  const totalListings = recentListings.length;

  return (
    <>
      <Helmet>
        <title>Giełda Aut Premium - Unikalne Samochody na Sprzedaż | Motoradar24</title>
        <meta name="description" content="Kupuj i sprzedawaj wyjątkowe samochody bez pośredników. Ekskluzywne ogłoszenia aut premium od pasjonatów. Sprawdzone oferty, bezpieczne transakcje." />
        <meta name="keywords" content="giełda aut premium, samochody na sprzedaż, auta ekskluzywne, sprzedaż aut, kupno samochodu, ogłoszenia motoryzacyjne" />
        
        <meta property="og:title" content="Giełda Aut Premium - Motoradar24" />
        <meta property="og:description" content={`${totalListings} aktywnych ogłoszeń. Kup lub sprzedaj wymarzone auto.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://motoradar24.pl/market" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-32">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white py-20 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-black mb-4">
              Giełda Aut Premium – Unikalne Samochody na Sprzedaż
            </h1>
            <p className="text-xl text-emerald-50 mb-8">
              Kup lub sprzedaj auto szybko i wygodnie • Sprawdzone ogłoszenia • Bez pośredników
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="bg-white rounded-2xl shadow-2xl p-2 flex flex-col md:flex-row gap-2">
              <div className="flex-1 flex items-center gap-2 px-4 py-3 bg-gray-50 rounded-xl">
                <Car className="text-gray-400" size={20}/>
                <input
                  type="text"
                  placeholder="Marka lub model..."
                  className="flex-1 bg-transparent outline-none text-gray-900 placeholder:text-gray-400"
                  value={searchBrand}
                  onChange={(e) => setSearchBrand(e.target.value)}
                />
              </div>
              <div className="flex-1 flex items-center gap-2 px-4 py-3 bg-gray-50 rounded-xl">
                <MapPin className="text-gray-400" size={20}/>
                <input
                  type="text"
                  placeholder="Lokalizacja..."
                  className="flex-1 bg-transparent outline-none text-gray-900 placeholder:text-gray-400"
                  value={searchLocation}
                  onChange={(e) => setSearchLocation(e.target.value)}
                />
              </div>
              <button
                type="submit"
                className="px-8 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all"
              >
                <Search size={20}/>
              </button>
            </form>
          </div>
        </div>

        {/* Recent Listings */}
        <div className="max-w-7xl mx-auto p-4 py-12">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-black text-gray-900 flex items-center gap-2">
                <TrendingUp className="text-emerald-600" size={28}/>
                Najnowsze ogłoszenia
              </h2>
              <p className="text-sm text-gray-500 mt-1">Świeżo dodane auta w najlepszych cenach</p>
              {pinnedCount > 0 && (
                <div className="mt-2 flex items-center gap-2 text-xs text-amber-600 font-semibold">
                  <Pin size={14} />
                  <span>{pinnedCount} {pinnedCount === 1 ? 'promowane ogłoszenie' : 'promowane ogłoszenia'}</span>
                </div>
              )}
            </div>
            <button
              onClick={() => navigate('/market/search')}
              className="text-sm font-semibold text-emerald-600 hover:text-emerald-700"
            >
              Zobacz wszystkie →
            </button>
          </div>

          {recentListings.length === 0 ? (
            <div className="text-center py-20">
              <Car className="mx-auto mb-4 text-gray-300" size={64}/>
              <p className="text-gray-500 font-semibold">Brak ogłoszeń</p>
              <p className="text-sm text-gray-400 mt-2">Dodaj pierwsze ogłoszenie!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recentListings.map(listing => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          )}
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-16 px-4 mt-12">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-black mb-4">Masz auto na sprzedaż?</h2>
            <p className="text-slate-300 mb-8">Dodaj ogłoszenie za darmo w mniej niż 3 minuty</p>
            <button
              onClick={() => navigate('/market/sell')}
              className="px-8 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all text-lg"
            >
              Dodaj ogłoszenie 🚀
            </button>
          </div>
        </div>
      </div>
    </>
  );
};